import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../../Components/Common/BreadCrumb";
import DayCloseTrendChart from "../../../../../Components/Studio/Financial/DayCloseTrendChart";
import { formatMoneyFromCents } from "../../../../../helpers/money";
import { resolvePaymentGatewayLabel } from "../../../../../helpers/paymentGatewayLabels";
import {
  DayCloseReport,
  DayCloseReportParams,
  clearDayCloseReviewed,
  downloadDayCloseCsv,
  downloadDayClosePdf,
  getDayCloseReport,
  markDayCloseReviewed,
  triggerBrowserDownload,
} from "../../../../../services/dayCloseReportService";
import {
  getProfessionals,
  Professional,
} from "../../../../../services/professionalService";
import { getSettings } from "../../../../../services/tenantSettingService";
import { getUnits, Unit } from "../../../../../services/unitService";

const UNITS_PAGE_SIZE = 100;
const PROFESSIONALS_PAGE_SIZE = 100;
const ALL_UNITS_VALUE = "all";

const todayIsoDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatDeltaLabel = (
  current: number,
  baseline: number,
  formatMoney: (cents: number) => string,
  t: (key: string, options?: Record<string, string>) => string,
  baselineKey: string
): string => {
  const delta = current - baseline;
  const abs = formatMoney(Math.abs(delta));
  const baselineLabel = t(baselineKey);

  if (delta > 0) {
    return t("studio.financial.dayClose.comparison.deltaUp", {
      value: abs,
      baseline: baselineLabel,
    });
  }

  if (delta < 0) {
    return t("studio.financial.dayClose.comparison.deltaDown", {
      value: abs,
      baseline: baselineLabel,
    });
  }

  return t("studio.financial.dayClose.comparison.deltaFlat", {
    baseline: baselineLabel,
  });
};

const DayCloseReportPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  document.title = t("studio.financial.dayClose.pageTitle");

  const formatMoney = useCallback(
    (cents: number) => formatMoneyFromCents(cents, i18n.language),
    [i18n.language]
  );

  const initialUnit = searchParams.get("consolidate")
    ? ALL_UNITS_VALUE
    : searchParams.get("unit_id") || "";

  const [units, setUnits] = useState<Unit[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [unitId, setUnitId] = useState(initialUnit);
  const [date, setDate] = useState(searchParams.get("date") || todayIsoDate());
  const [professionalId, setProfessionalId] = useState(
    searchParams.get("professional_id") || ""
  );
  const [cashRegisterMode, setCashRegisterMode] = useState<
    "unified" | "per_professional"
  >("unified");
  const [report, setReport] = useState<DayCloseReport | null>(null);
  const [loadingUnits, setLoadingUnits] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [exporting, setExporting] = useState<"csv" | "pdf" | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [markingReviewed, setMarkingReviewed] = useState(false);
  const [cashCountInput, setCashCountInput] = useState("");

  const showProfessionalFilter = cashRegisterMode === "per_professional";
  const isConsolidate = unitId === ALL_UNITS_VALUE;
  const canConsolidate = units.length > 1;

  const resolveUnitName = useCallback(
    (id: number | null): string => {
      if (id === null) {
        return t("studio.financial.dayClose.fields.unit.all");
      }

      return units.find((unit) => unit.id === id)?.name ?? String(id);
    },
    [units, t]
  );

  const resolveMethodLabel = (method: string): string => {
    const key = `studio.orders.checkout.payment.method.${method}`;
    const translated = t(key);

    return translated === key ? method : translated;
  };

  const syncUrl = useCallback(
    (next: { unitId: string; date: string; professionalId: string }) => {
      const params = new URLSearchParams();
      if (next.unitId === ALL_UNITS_VALUE) {
        params.set("consolidate", "1");
      } else if (next.unitId) {
        params.set("unit_id", next.unitId);
      }
      if (next.date) {
        params.set("date", next.date);
      }
      if (next.professionalId) {
        params.set("professional_id", next.professionalId);
      }
      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  const loadBootstrap = useCallback(async () => {
    setLoadingUnits(true);
    try {
      const [unitsResponse, settingsResponse, professionalsResponse] =
        await Promise.all([
          getUnits({ per_page: UNITS_PAGE_SIZE }),
          getSettings(),
          getProfessionals({ per_page: PROFESSIONALS_PAGE_SIZE }),
        ]);

      setUnits(unitsResponse.data);
      setProfessionals(professionalsResponse.data);
      setCashRegisterMode(
        settingsResponse.data.cash_register_mode === "per_professional"
          ? "per_professional"
          : "unified"
      );

      if (!unitId && unitsResponse.data.length === 1) {
        setUnitId(String(unitsResponse.data[0].id));
      }
    } catch (error: any) {
      toast.error(
        error?.data || t("studio.financial.dayClose.toast.loadUnitsError")
      );
    } finally {
      setLoadingUnits(false);
    }
  }, [t, unitId]);

  const buildParams = useCallback((): DayCloseReportParams | null => {
    if (!date) {
      return null;
    }

    if (isConsolidate) {
      return {
        date,
        consolidate: true,
        ...(showProfessionalFilter && professionalId
          ? { professional_id: Number(professionalId) }
          : {}),
      };
    }

    if (!unitId) {
      return null;
    }

    return {
      date,
      unit_id: Number(unitId),
      ...(showProfessionalFilter && professionalId
        ? { professional_id: Number(professionalId) }
        : {}),
    };
  }, [unitId, date, professionalId, showProfessionalFilter, isConsolidate]);

  const fetchReport = useCallback(async () => {
    const params = buildParams();
    if (!params) {
      toast.error(t("studio.financial.dayClose.validation.filtersRequired"));
      return;
    }

    setLoadingReport(true);
    setHasSearched(true);
    syncUrl({ unitId, date, professionalId });

    try {
      const response = await getDayCloseReport(params);
      setReport(response.data);
      toast.success(
        response.message || t("studio.financial.dayClose.toast.generated")
      );
    } catch (error: any) {
      setReport(null);
      toast.error(error?.data || t("studio.financial.dayClose.toast.loadError"));
    } finally {
      setLoadingReport(false);
    }
  }, [unitId, date, professionalId, buildParams, syncUrl, t]);

  const handleExport = useCallback(
    async (format: "csv" | "pdf") => {
      const params = buildParams();
      if (!params) {
        toast.error(t("studio.financial.dayClose.validation.filtersRequired"));
        return;
      }

      setExporting(format);

      try {
        const blob =
          format === "csv"
            ? await downloadDayCloseCsv(params)
            : await downloadDayClosePdf(params);
        const unitPart = isConsolidate
          ? "todas-unidades"
          : `unidade-${unitId}`;
        const filename = `fechamento-dia-${date}-${unitPart}.${format}`;
        triggerBrowserDownload(blob, filename);
        toast.success(t(`studio.financial.dayClose.toast.export.${format}`));
      } catch (error: any) {
        toast.error(
          error?.data || t("studio.financial.dayClose.toast.exportError")
        );
      } finally {
        setExporting(null);
      }
    },
    [buildParams, date, isConsolidate, unitId, t]
  );

  const handleMarkReviewed = useCallback(async () => {
    if (!date) {
      return;
    }

    const params = buildParams();
    if (!params) {
      toast.error(t("studio.financial.dayClose.validation.filtersRequired"));
      return;
    }

    let cashCountedCents: number | null = null;
    if (cashCountInput.trim() !== "") {
      const parsed = Number(cashCountInput.replace(",", "."));
      if (!Number.isFinite(parsed) || parsed < 0) {
        toast.error(t("studio.financial.dayClose.cashCount.invalid"));
        return;
      }
      cashCountedCents = Math.round(parsed * 100);
    }

    setMarkingReviewed(true);
    try {
      const response = await markDayCloseReviewed({
        date,
        ...(params.consolidate
          ? { consolidate: true }
          : { unit_id: params.unit_id }),
        cash_counted_cents: cashCountedCents,
      });
      setReport((current) =>
        current
          ? {
              ...current,
              ...response.data,
              day_fully_reviewed:
                params.consolidate || current.day_fully_reviewed,
            }
          : current
      );
      try {
        if (response.data.reviewed) {
          localStorage.setItem(
            `adatis.dayClose.reviewed.${date}.${params.consolidate ? "all" : params.unit_id}`,
            "1"
          );
        }
      } catch {
        // ignore
      }
      toast.success(
        response.message || t("studio.financial.dayClose.toast.reviewed")
      );
    } catch (error: any) {
      toast.error(
        error?.data || t("studio.financial.dayClose.toast.reviewedError")
      );
    } finally {
      setMarkingReviewed(false);
    }
  }, [date, cashCountInput, buildParams, t]);

  const handleClearReviewed = useCallback(async () => {
    if (!date) {
      return;
    }

    const params = buildParams();
    if (!params) {
      toast.error(t("studio.financial.dayClose.validation.filtersRequired"));
      return;
    }

    setMarkingReviewed(true);
    try {
      const response = await clearDayCloseReviewed({
        date,
        ...(params.consolidate
          ? { consolidate: true }
          : { unit_id: params.unit_id }),
      });
      setReport((current) =>
        current
          ? {
              ...current,
              ...response.data,
              day_fully_reviewed: false,
            }
          : current
      );
      try {
        localStorage.removeItem(
          `adatis.dayClose.reviewed.${date}.${params.consolidate ? "all" : params.unit_id}`
        );
      } catch {
        // ignore
      }
      toast.success(
        response.message || t("studio.financial.dayClose.toast.cleared")
      );
    } catch (error: any) {
      toast.error(
        error?.data || t("studio.financial.dayClose.toast.clearedError")
      );
    } finally {
      setMarkingReviewed(false);
    }
  }, [date, buildParams, t]);

  useEffect(() => {
    if (!report) {
      return;
    }
    if (
      report.cash_counted_cents !== null &&
      report.cash_counted_cents !== undefined
    ) {
      setCashCountInput((report.cash_counted_cents / 100).toFixed(2));
    } else {
      setCashCountInput("");
    }
  }, [report]);

  useEffect(() => {
    loadBootstrap();
  }, [loadBootstrap]);

  useEffect(() => {
    const hasDeepLink =
      searchParams.get("unit_id") || searchParams.get("consolidate");
    if (!loadingUnits && date && hasDeepLink && (unitId || isConsolidate)) {
      void fetchReport();
    }
    // Auto-run once when deep-linked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingUnits]);

  const anomalyAlert = useMemo(() => {
    if (!report?.anomaly?.flagged) {
      return null;
    }

    const directionKey =
      report.anomaly.direction === "up"
        ? "studio.financial.dayClose.anomaly.up"
        : "studio.financial.dayClose.anomaly.down";

    return (
      <Alert color="warning" className="mb-3">
        <strong className="d-block mb-1">
          {t("studio.financial.dayClose.anomaly.title")}
        </strong>
        {t(directionKey, {
          percent: String(report.anomaly.threshold_percent),
          delta: formatMoney(Math.abs(report.anomaly.delta_vs_week_avg_cents)),
        })}
      </Alert>
    );
  }, [report, formatMoney, t]);

  const comparisonCards = useMemo(() => {
    if (!report?.comparison) {
      return null;
    }

    return (
      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader>
              <h5 className="card-title mb-0">
                {t("studio.financial.dayClose.comparison.title")}
              </h5>
            </CardHeader>
            <CardBody>
              <Row className="g-3">
                <Col md={6}>
                  <p className="text-muted mb-1">
                    {t("studio.financial.dayClose.comparison.yesterday", {
                      date: report.comparison.yesterday.date,
                    })}
                  </p>
                  <h5 className="mb-1">
                    {formatMoney(report.comparison.yesterday.net_cents)}
                  </h5>
                  <p className="text-muted small mb-0">
                    {formatDeltaLabel(
                      report.totals.net_cents,
                      report.comparison.yesterday.net_cents,
                      formatMoney,
                      t,
                      "studio.financial.dayClose.comparison.baselineYesterday"
                    )}
                  </p>
                </Col>
                <Col md={6}>
                  <p className="text-muted mb-1">
                    {t("studio.financial.dayClose.comparison.weekAvg")}
                  </p>
                  <h5 className="mb-1">
                    {formatMoney(report.comparison.week_avg.net_cents)}
                  </h5>
                  <p className="text-muted small mb-0">
                    {formatDeltaLabel(
                      report.totals.net_cents,
                      report.comparison.week_avg.net_cents,
                      formatMoney,
                      t,
                      "studio.financial.dayClose.comparison.baselineWeek"
                    )}
                  </p>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    );
  }, [report, formatMoney, t]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("studio.financial.dayClose.breadcrumb.title")}
            pageTitle={t("studio.financial.dayClose.breadcrumb.pageTitle")}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">
                    {t("studio.financial.dayClose.filters.title")}
                  </h5>
                </CardHeader>
                <CardBody>
                  <Row className="g-3 align-items-end">
                    <Col md={showProfessionalFilter ? 3 : 4}>
                      <Label htmlFor="day-close-unit">
                        {t("studio.financial.dayClose.fields.unit.label")}
                      </Label>
                      <Input
                        id="day-close-unit"
                        type="select"
                        value={unitId}
                        disabled={loadingUnits}
                        onChange={(event) => setUnitId(event.target.value)}
                      >
                        <option value="">
                          {t(
                            "studio.financial.dayClose.fields.unit.placeholder"
                          )}
                        </option>
                        {canConsolidate ? (
                          <option value={ALL_UNITS_VALUE}>
                            {t("studio.financial.dayClose.fields.unit.all")}
                          </option>
                        ) : null}
                        {units.map((unit) => (
                          <option key={unit.id} value={unit.id}>
                            {unit.name}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={showProfessionalFilter ? 3 : 3}>
                      <Label htmlFor="day-close-date">
                        {t("studio.financial.dayClose.fields.date.label")}
                      </Label>
                      <Input
                        id="day-close-date"
                        type="date"
                        value={date}
                        onChange={(event) => setDate(event.target.value)}
                      />
                    </Col>
                    {showProfessionalFilter ? (
                      <Col md={3}>
                        <Label htmlFor="day-close-professional">
                          {t(
                            "studio.financial.dayClose.fields.professional.label"
                          )}
                        </Label>
                        <Input
                          id="day-close-professional"
                          type="select"
                          value={professionalId}
                          disabled={loadingUnits}
                          onChange={(event) =>
                            setProfessionalId(event.target.value)
                          }
                        >
                          <option value="">
                            {t(
                              "studio.financial.dayClose.fields.professional.placeholder"
                            )}
                          </option>
                          {professionals.map((professional) => (
                            <option
                              key={professional.id}
                              value={professional.id}
                            >
                              {professional.name}
                            </option>
                          ))}
                        </Input>
                        <p className="text-muted small mb-0 mt-1">
                          {t(
                            "studio.financial.dayClose.fields.professional.help"
                          )}
                        </p>
                      </Col>
                    ) : null}
                    <Col
                      md={showProfessionalFilter ? 3 : 5}
                      className="d-flex flex-wrap gap-2"
                    >
                      <Button
                        color="primary"
                        onClick={fetchReport}
                        disabled={loadingReport || loadingUnits}
                      >
                        {loadingReport ? (
                          <Spinner size="sm" className="me-1" />
                        ) : null}
                        {t("studio.financial.dayClose.actions.generate")}
                      </Button>
                      <Button
                        color="soft-success"
                        outline
                        onClick={() => handleExport("csv")}
                        disabled={!!exporting || loadingUnits}
                      >
                        {exporting === "csv" ? (
                          <Spinner size="sm" className="me-1" />
                        ) : null}
                        {t("studio.financial.dayClose.actions.exportCsv")}
                      </Button>
                      <Button
                        color="soft-danger"
                        outline
                        onClick={() => handleExport("pdf")}
                        disabled={!!exporting || loadingUnits}
                      >
                        {exporting === "pdf" ? (
                          <Spinner size="sm" className="me-1" />
                        ) : null}
                        {t("studio.financial.dayClose.actions.exportPdf")}
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {loadingReport ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="text-muted mt-2 mb-0">
                {t("studio.financial.dayClose.loading")}
              </p>
            </div>
          ) : null}

          {!loadingReport && report ? (
            <>
              {anomalyAlert}

              {report.trend && report.trend.length > 0 ? (
                <Row>
                  <Col lg={12}>
                    <Card>
                      <CardHeader>
                        <h5 className="card-title mb-0">
                          {t("studio.financial.dayClose.trend.title")}
                        </h5>
                      </CardHeader>
                      <CardBody>
                        <DayCloseTrendChart
                          trend={report.trend}
                          currencyLocale={i18n.language}
                        />
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              ) : null}

              <Row>
                <Col lg={12}>
                  <Card>
                    <CardHeader>
                      <h5 className="card-title mb-0">
                        {t("studio.financial.dayClose.review.title")}
                      </h5>
                    </CardHeader>
                    <CardBody>
                      <Row className="g-3 align-items-end">
                        <Col md={4}>
                          <Label htmlFor="day-close-cash-count">
                            {t("studio.financial.dayClose.cashCount.label")}
                          </Label>
                          <Input
                            id="day-close-cash-count"
                            type="number"
                            min="0"
                            step="0.01"
                            value={cashCountInput}
                            disabled={!!report.reviewed}
                            onChange={(event) =>
                              setCashCountInput(event.target.value)
                            }
                            placeholder={t(
                              "studio.financial.dayClose.cashCount.placeholder"
                            )}
                          />
                          <p className="text-muted small mb-0 mt-1">
                            {t("studio.financial.dayClose.cashCount.system", {
                              value: formatMoney(
                                report.cash_system_cents ?? 0
                              ),
                            })}
                          </p>
                        </Col>
                        <Col md={4}>
                          {(() => {
                            let diff = report.cash_difference_cents;
                            if (
                              !report.reviewed &&
                              cashCountInput.trim() !== ""
                            ) {
                              const parsed = Number(
                                cashCountInput.replace(",", ".")
                              );
                              if (Number.isFinite(parsed) && parsed >= 0) {
                                diff =
                                  Math.round(parsed * 100) -
                                  (report.cash_system_cents ?? 0);
                              }
                            }

                            if (diff === null || diff === undefined) {
                              return (
                                <p className="text-muted mb-0">
                                  {t(
                                    "studio.financial.dayClose.cashCount.help"
                                  )}
                                </p>
                              );
                            }

                            return (
                              <Alert
                                color={diff === 0 ? "success" : "warning"}
                                className="mb-0"
                              >
                                {diff === 0
                                  ? t(
                                      "studio.financial.dayClose.cashCount.match"
                                    )
                                  : t(
                                      "studio.financial.dayClose.cashCount.diff",
                                      {
                                        value: formatMoney(Math.abs(diff)),
                                        direction:
                                          diff > 0
                                            ? t(
                                                "studio.financial.dayClose.cashCount.over"
                                              )
                                            : t(
                                                "studio.financial.dayClose.cashCount.under"
                                              ),
                                      }
                                    )}
                              </Alert>
                            );
                          })()}
                        </Col>
                        <Col md={4} className="d-flex flex-wrap gap-2">
                          {report.reviewed ? (
                            <>
                              <div className="flex-grow-1">
                                <span className="badge bg-success-subtle text-success mb-1">
                                  {t(
                                    "studio.financial.dayClose.reviewed.badge"
                                  )}
                                </span>
                                <p className="text-muted small mb-0">
                                  {t(
                                    "studio.financial.dayClose.reviewed.byline",
                                    {
                                      name:
                                        report.reviewed_by?.name ??
                                        t(
                                          "studio.financial.dayClose.reviewed.unknown"
                                        ),
                                      at: report.reviewed_at
                                        ? new Date(
                                            report.reviewed_at
                                          ).toLocaleString(i18n.language)
                                        : "—",
                                    }
                                  )}
                                </p>
                              </div>
                              <Button
                                color="soft-secondary"
                                size="sm"
                                disabled={markingReviewed}
                                onClick={handleClearReviewed}
                              >
                                {markingReviewed ? (
                                  <Spinner size="sm" className="me-1" />
                                ) : null}
                                {t(
                                  "studio.financial.dayClose.actions.clearReviewed"
                                )}
                              </Button>
                            </>
                          ) : (
                            <Button
                              color="success"
                              disabled={markingReviewed}
                              onClick={handleMarkReviewed}
                            >
                              {markingReviewed ? (
                                <Spinner size="sm" className="me-1" />
                              ) : null}
                              {t(
                                "studio.financial.dayClose.actions.markReviewed"
                              )}
                            </Button>
                          )}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {report.commissions?.enabled ? (
                <Row className="mb-3">
                  <Col lg={12}>
                    <Card className="border border-success border-opacity-25">
                      <CardBody className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                        <div>
                          <p className="text-muted mb-1 fs-13">
                            {t("studio.financial.dayClose.commissions.title")}
                          </p>
                          <h4 className="mb-1 fw-semibold">
                            {formatMoney(report.commissions.total_cents)}
                          </h4>
                          <p className="text-muted small mb-0">
                            {report.commissions.totals_match
                              ? t("studio.financial.dayClose.commissions.match", {
                                  count: report.commissions.lines_count,
                                  total: formatMoney(report.commissions.total_cents),
                                })
                              : t("studio.financial.dayClose.commissions.mismatch")}
                          </p>
                        </div>
                        <Link
                          to="/professionals"
                          className="btn btn-soft-success btn-sm"
                        >
                          {t("studio.financial.dayClose.commissions.openProfessionals")}
                        </Link>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              ) : null}

              <Row>
                <Col md={3}>
                  <Card className="card-animate">
                    <CardBody>
                      <p className="text-uppercase fw-medium text-muted mb-0">
                        {t("studio.financial.dayClose.cards.gross")}
                      </p>
                      <h4 className="fs-22 fw-semibold mb-0">
                        {formatMoney(report.totals.gross_cents)}
                      </h4>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="card-animate">
                    <CardBody>
                      <p className="text-uppercase fw-medium text-muted mb-0">
                        {t("studio.financial.dayClose.cards.fees")}
                      </p>
                      <h4 className="fs-22 fw-semibold mb-0">
                        {formatMoney(report.totals.fee_cents)}
                      </h4>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="card-animate">
                    <CardBody>
                      <p className="text-uppercase fw-medium text-muted mb-0">
                        {t("studio.financial.dayClose.cards.net")}
                      </p>
                      <h4 className="fs-22 fw-semibold mb-0">
                        {formatMoney(report.totals.net_cents)}
                      </h4>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="card-animate">
                    <CardBody>
                      <p className="text-uppercase fw-medium text-muted mb-0">
                        {t("studio.financial.dayClose.cards.orders")}
                      </p>
                      <h4 className="fs-22 fw-semibold mb-0">
                        {report.totals.orders_count}
                      </h4>
                      <p className="text-muted mb-0 mt-1">
                        {t("studio.financial.dayClose.cards.paymentsCount", {
                          count: report.totals.payments_count,
                        })}
                      </p>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {comparisonCards}

              {report.consolidated && report.units && report.units.length > 0 ? (
                <Row>
                  <Col lg={12}>
                    <Card>
                      <CardHeader>
                        <h5 className="card-title mb-0">
                          {t("studio.financial.dayClose.tables.byUnit")}
                        </h5>
                      </CardHeader>
                      <CardBody>
                        <div className="table-responsive">
                          <Table className="align-middle table-nowrap mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>
                                  {t("studio.financial.dayClose.tables.unit")}
                                </th>
                                <th className="text-end">
                                  {t("studio.financial.dayClose.tables.gross")}
                                </th>
                                <th className="text-end">
                                  {t("studio.financial.dayClose.tables.net")}
                                </th>
                                <th className="text-end">
                                  {t(
                                    "studio.financial.dayClose.tables.orders"
                                  )}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {report.units.map((unitRow) => (
                                <tr key={unitRow.unit_id}>
                                  <td>
                                    <Button
                                      color="link"
                                      className="p-0 fw-medium"
                                      onClick={() => {
                                        setUnitId(String(unitRow.unit_id));
                                        syncUrl({
                                          unitId: String(unitRow.unit_id),
                                          date: report.date,
                                          professionalId,
                                        });
                                        void (async () => {
                                          setLoadingReport(true);
                                          setHasSearched(true);
                                          try {
                                            const response =
                                              await getDayCloseReport({
                                                date: report.date,
                                                unit_id: unitRow.unit_id,
                                                ...(showProfessionalFilter &&
                                                professionalId
                                                  ? {
                                                      professional_id:
                                                        Number(professionalId),
                                                    }
                                                  : {}),
                                              });
                                            setReport(response.data);
                                          } catch (error: any) {
                                            toast.error(
                                              error?.data ||
                                                t(
                                                  "studio.financial.dayClose.toast.loadError"
                                                )
                                            );
                                          } finally {
                                            setLoadingReport(false);
                                          }
                                        })();
                                      }}
                                    >
                                      {resolveUnitName(unitRow.unit_id)}
                                    </Button>
                                  </td>
                                  <td className="text-end">
                                    {formatMoney(unitRow.totals.gross_cents)}
                                  </td>
                                  <td className="text-end">
                                    {formatMoney(unitRow.totals.net_cents)}
                                  </td>
                                  <td className="text-end">
                                    {unitRow.totals.orders_count}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              ) : null}

              <Row>
                <Col lg={12}>
                  <Card>
                    <CardHeader>
                      <h5 className="card-title mb-0">
                        {t("studio.financial.dayClose.tables.byMethod")}
                      </h5>
                      <p className="text-muted mb-0 mt-1">
                        {t("studio.financial.dayClose.summary.subtitle", {
                          unit: resolveUnitName(report.unit_id),
                          date: report.date,
                          timezone: report.timezone,
                        })}
                      </p>
                    </CardHeader>
                    <CardBody>
                      {report.by_method.length === 0 ? (
                        <p className="text-muted mb-0">
                          {t("studio.financial.dayClose.empty.noPayments")}
                        </p>
                      ) : (
                        <div className="table-responsive">
                          <Table className="align-middle table-nowrap mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>
                                  {t(
                                    "studio.financial.dayClose.tables.method"
                                  )}
                                </th>
                                <th>
                                  {t(
                                    "studio.financial.dayClose.tables.gateway"
                                  )}
                                </th>
                                <th className="text-end">
                                  {t("studio.financial.dayClose.tables.gross")}
                                </th>
                                <th className="text-end">
                                  {t("studio.financial.dayClose.tables.fees")}
                                </th>
                                <th className="text-end">
                                  {t("studio.financial.dayClose.tables.net")}
                                </th>
                                <th className="text-end">
                                  {t(
                                    "studio.financial.dayClose.tables.payments"
                                  )}
                                </th>
                                <th className="text-end">
                                  {t(
                                    "studio.financial.dayClose.tables.orders"
                                  )}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {report.by_method.map((line) => (
                                <tr
                                  key={`${line.payment_method}-${line.gateway_slug ?? "none"}`}
                                >
                                  <td>
                                    {resolveMethodLabel(line.payment_method)}
                                  </td>
                                  <td>
                                    {resolvePaymentGatewayLabel(
                                      line.gateway_slug,
                                      t
                                    )}
                                  </td>
                                  <td className="text-end">
                                    {formatMoney(line.amount_cents)}
                                  </td>
                                  <td className="text-end">
                                    {formatMoney(line.fee_cents)}
                                  </td>
                                  <td className="text-end">
                                    {formatMoney(line.net_cents)}
                                  </td>
                                  <td className="text-end">
                                    {line.payments_count}
                                  </td>
                                  <td className="text-end">
                                    {line.orders_count}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          ) : null}

          {!loadingReport && !report && !hasSearched ? (
            <Card>
              <CardBody>
                <p className="text-muted mb-0">
                  {t("studio.financial.dayClose.empty.initial")}
                </p>
              </CardBody>
            </Card>
          ) : null}
        </Container>
      </div>
      <ToastContainer closeButton={false} limit={1} />
    </React.Fragment>
  );
};

export default DayCloseReportPage;
